import { Fragment, ReactNode, useState } from "react";
import { Box, Divider, Grid, Typography } from "@mui/material";
import { ComplianceBreadcrumbs } from "~/pages/compliance/components/ComplianceBreadcrumbs";
import { Space } from "~/lib/types";
import { Loading, LoadingFailed } from "~/components/loading";
import { Code } from "~/components/code";
import { FilterBar } from "~/components/FilterBar/FilterBar";
import { DataQueryListView } from "~/components/DataQueries/DataQueryListView";
import { DataQueryGridView } from "~/components/DataQueries/DataQueryGridView";
import { EmptyState } from "~/components/empty-state/empty-state";
import { ViewSwitcher, ViewType } from "~/components/view-switcher";
import { useSearchParams } from "react-router-dom";
import { INITIAL_PAGE_RANGE } from "~/components/pagination";
import {
  AssetOrderField,
  MqueryAssetScoresQuery,
  OrderDirection,
  useMqueryAssetScoresQuery,
} from "~/operations";
import { Markdown } from "~/components/markdown";
import { useSearch } from "~/components/search/useSearch";
import { Header } from "~/types/table";

export type MqueryAssetScoresConnection = NonNullable<
  MqueryAssetScoresQuery["mqueryAssetScores"]
>;
export type MQueryAssetScoresEdges = NonNullable<
  MqueryAssetScoresConnection["edges"]
>;
export type MQueryAssetScoresEdge = MQueryAssetScoresEdges[0];
export type MQueryAssetScoresNode = NonNullable<MQueryAssetScoresEdge["node"]>;
export type MQueryAssetScore = Extract<
  MQueryAssetScoresNode,
  { __typename: "QueryAssetScore" }
>;

export type DataQueryProps = {
  space: Space;
};

export function DataQuery({ space }: DataQueryProps) {
  const [pageItems, setPageItems] = useState(INITIAL_PAGE_RANGE);
  const [searchParams] = useSearchParams();
  const search = useSearch();
  const queryMrn = searchParams.get("queryMrn") as string;
  const selectedView = searchParams.get("view") || "list";

  const {
    data: { mqueryAssetScores } = {},
    error,
    loading,
    fetchMore,
  } = useMqueryAssetScoresQuery({
    skip: !queryMrn,
    variables: {
      input: {
        // after: null,
        // limit: null,
        // orderBy: {
        //   direction: OrderDirection.Desc,
        //   field: AssetOrderField.LastUpdated,
        // },
        query: null,
        queryMrn: queryMrn,
        scopeMrn: space.mrn,
      },
      first: 50,
      after: null,
      orderBy: {
        direction:
          (searchParams.get("direction") as OrderDirection) ||
          OrderDirection.Desc,
        field: AssetOrderField.Name,
      },
    },
  });

  if (error) {
    return <LoadingFailed what="Assets" />;
  }

  if (loading && !mqueryAssetScores) {
    return <Loading what="Assets" />;
  }

  const { edges } = mqueryAssetScores || {};
  const assets =
    edges?.flatMap((edge) =>
      edge.node?.__typename === "QueryAssetScore" ? edge.node : [],
    ) || [];

  const totalCount = mqueryAssetScores?.totalCount || 0;
  let copyAssets = [...assets];

  // Filter assets by Asset name only until we get more robust filtering
  // from the API
  if (search.searchFilters.length > 0) {
    copyAssets = copyAssets?.filter((asset) => {
      return asset.assetName
        ?.toLowerCase()
        .includes(search.searchFilters.join(" ").toLowerCase());
    });
  }

  const handleView = () => {
    if (copyAssets.length === 0) {
      return <NoAssetsFound />;
    }

    if (selectedView === "list") {
      return (
        <Grid size={12}>
          <DataQueryListView
            assets={copyAssets}
            pageItems={pageItems}
            tableHeaders={tableHeaders}
            space={space}
            queryMrn={queryMrn}
            paginationProps={{
              totalCount,
              setPageItems,
              pageInfo: mqueryAssetScores?.pageInfo,
              fetchMore,
            }}
          />
        </Grid>
      );
    }

    if (selectedView === "keyboard") {
      return (
        <DataQueryGridView
          assets={copyAssets}
          space={space}
          queryMrn={queryMrn}
        />
      );
    }
  };

  return (
    <Grid container rowGap={2}>
      <Grid size={12}>
        <ComplianceBreadcrumbs space={space} />
      </Grid>
      <Grid size={12}>
        <Typography variant="h4" fontWeight={700} sx={{ mb: 1 }}>
          {mqueryAssetScores?.title}
        </Typography>
        <Markdown
          sx={{ color: "text.secondary" }}
          source={mqueryAssetScores?.description || ""}
        />
      </Grid>
      <Grid size={12}>
        <TitleDivider title="Queries">
          <Grid size={12} mt={-2}>
            <Code className="javascript" copyButton>
              {mqueryAssetScores?.mql}
            </Code>
          </Grid>
        </TitleDivider>
      </Grid>
      <Grid size={12}>
        <TitleDivider title="Assets">
          <Grid size={12} container rowGap={3}>
            {!assets || assets?.length === 0 ? (
              <Grid size={12}>
                <EmptyState contentType="data-query-assets" />
              </Grid>
            ) : (
              <Fragment>
                <Grid size={12}>
                  <FilterBar
                    searchId="compliance-data-query-assets"
                    placeholder="compliance_data_query_assets"
                    search={search}
                    mb={0}
                  />
                </Grid>

                <Grid size={12} sx={{ textAlign: "right" }}>
                  <ViewSwitcher
                    views={[ViewType.List, ViewType.Keyboard]}
                    defaultView={ViewType.List}
                  />
                </Grid>
                {handleView()}
              </Fragment>
            )}
          </Grid>
        </TitleDivider>
      </Grid>
    </Grid>
  );
}

type TitleDividerProps = {
  title: string;
  children: ReactNode;
};

const TitleDivider = ({ title, children }: TitleDividerProps) => {
  return (
    <Grid container alignItems="center" rowGap={3}>
      <Grid sx={{ display: "flex", alignItems: "center" }}>
        <Box>
          <Typography
            variant="h5"
            sx={{
              display: "inline-block",
              pr: 3,
              pb: 1,
              fontWeight: 700,
            }}
          >
            {title}
          </Typography>
        </Box>
      </Grid>
      <Grid size="grow">
        <Divider />
      </Grid>
      {children}
    </Grid>
  );
};

const tableHeaders: Header[] = [
  {
    id: "NAME",
    label: "Name",
    options: { width: "60%" },
  },
  {
    id: "PLATFORM",
    label: "Platform",
  },
  {
    id: "EXPAND",
    label: "",
    options: { width: 75 },
  },
];

export const NoAssetsFound = () => {
  return (
    <Grid container>
      <Grid
        sx={{
          py: 10,
          border: "1px solid",
          borderColor: "text.secondary",
          borderStyle: "dashed",
          borderRadius: "4px",
        }}
        size={12}
      >
        <Typography color="text.secondary" textAlign="center">
          No assets found, please try using a different filter.
        </Typography>
      </Grid>
    </Grid>
  );
};
